<script setup lang="ts">
  import { VButton } from "@krainovsd/vue-ui";
  import { ref } from "vue";
  import { useI18n } from "vue-i18n";

  type Props = {
    title: string;
    subtitle?: string;
    back?: boolean;
    next?: boolean;
    submit?: boolean;
    // Blocks the next/submit button when the step's input is invalid.
    disabled?: boolean;
    // Optional label overrides — default to the report-wizard wording.
    backLabel?: string;
    nextLabel?: string;
    submitLabel?: string;
  };
  defineProps<Props>();
  const emit = defineEmits<{ back: []; next: []; submit: [] }>();

  const { t } = useI18n();

  const rootEl = ref<HTMLElement | null>(null);
  defineExpose({ rootEl });
</script>

<template>
  <section ref="rootEl" :class="$style.step">
    <div :class="$style.stepHeader">
      <h2 :class="$style.stepTitle">{{ title }}</h2>
      <span v-if="subtitle" :class="$style.stepSubtitle">{{ subtitle }}</span>
    </div>

    <div :class="$style.content">
      <slot />
    </div>

    <div :class="$style.buttons">
      <VButton v-if="back" type="text" size="large" @click="emit('back')">
        {{ backLabel ?? t("page.createReport.back", {}, { default: "Назад" }) }}
      </VButton>
      <span v-else></span>

      <VButton
        v-if="next"
        :class="$style.primaryButton"
        type="primary"
        size="large"
        :disabled="disabled"
        @click="emit('next')"
      >
        {{ nextLabel ?? t("page.createReport.next", {}, { default: "Далее" }) }}
      </VButton>
      <VButton
        v-else-if="submit"
        :class="$style.primaryButton"
        type="primary"
        size="large"
        :disabled="disabled"
        @click="emit('submit')"
      >
        {{ submitLabel ?? t("page.createReport.create", {}, { default: "Построить отчёт" }) }}
      </VButton>
    </div>
  </section>
</template>

<style lang="scss" module>
  .step {
    min-height: 100%;
    flex-shrink: 0;
    padding: 16px 0 80px;

    scroll-snap-align: start;
    scroll-snap-stop: always;

    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 16px;
  }

  .stepHeader {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .stepTitle {
    margin: 0;

    color: var(--foreground);
    font-weight: 600;
    font-size: 1.5rem;
    line-height: 2rem;
  }

  .stepSubtitle {
    color: var(--muted-foreground);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .buttons {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  .primaryButton {
    padding-left: 2rem;
    padding-right: 2rem;
  }
</style>
